import { httpClient } from './httpClient';
import { config } from '@/config/appConfig';
import { auditLogger } from '../auth/auditLogger';

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
}

interface RequestConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  cache?: boolean;
  cacheTTL?: number;
  skipErrorHandler?: boolean;
  timeout?: number;
}

class ApiClient {
  private getToken: () => string | null;
  private refreshToken: () => Promise<boolean>;
  private logout: () => Promise<void>;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor() {
    // We'll set these methods after authService is initialized
    this.getToken = () => null;
    this.refreshToken = async () => false;
    this.logout = async () => {};
  }

  /**
   * Set authentication methods (called by authService to avoid circular dependency)
   */
  setAuthMethods(getToken: () => string | null, refreshToken: () => Promise<boolean>, logout: () => Promise<void>) {
    this.getToken = getToken;
    this.refreshToken = refreshToken;
    this.logout = logout;
  }

  /**
   * Make API request with caching and error handling
   */
  async request<T = any>(requestConfig: RequestConfig): Promise<T> {
    const {
      url,
      method,
      data,
      params,
      headers = {},
      cache = true,
      cacheTTL = config.cache.defaultTTL,
      skipErrorHandler = false,
      timeout = config.api.timeout || 30000,
    } = requestConfig;

    // Add authentication headers
    const token = this.getToken();
    if (!headers.Authorization) {
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else if (config.api.staticAuth) {
        headers.Authorization = config.api.staticAuth;
      }
    }

    try {
      const result = await httpClient.request<T>({
        url,
        method,
        data,
        params,
        headers,
        cache,
        cacheTTL,
        skipErrorHandler,
        timeout,
      });

      // Log successful API call
      await auditLogger.logUserAction('API_CALL', {
        url,
        method,
        status: 'success',
      });

      return result;
    } catch (error: any) {
      // Log failed API call
      await auditLogger.logUserAction('API_CALL_FAILED', {
        url,
        method,
        error: error?.message || 'Unknown error',
        status: error?.response?.status || 'unknown',
      });

      // ✅ Handle 403 Forbidden - Token Refresh Flow
      // 403 can occur if:
      // 1. Token has expired (backend validates expiry)
      // 2. User lacks permissions
      // Try refresh first, then determine if it's a permission issue
      if (error?.response?.status === 403 && !skipErrorHandler) {
        console.log('⚠️ 403 Forbidden - attempting token refresh via Keycloak...');
        
        // Prevent infinite retry loop - check if this is already a retry
        const isRetry = (requestConfig as any)._isRetry;
        if (isRetry) {
          console.warn('⚠️ Already retried after token refresh - insufficient permissions or token issue');
          throw error;
        }
        
        // Handle concurrent refresh attempts - use shared promise
        if (this.isRefreshing && this.refreshPromise) {
          console.log('🔄 Token refresh already in progress, waiting...');
          await this.refreshPromise;
        } else if (!this.isRefreshing) {
          this.isRefreshing = true;
          this.refreshPromise = this.refreshToken();
          try {
            await this.refreshPromise;
          } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
          }
        }
        
        // Try to get new token and retry
        const newToken = this.getToken();
        if (newToken) {
          console.log('✅ Token refreshed, retrying request...');
          const newHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
          
          try {
            // Mark this as a retry to prevent infinite loops
            const retryConfig = { 
              ...requestConfig, 
              headers: newHeaders
            };
            (retryConfig as any)._isRetry = true;
            return await httpClient.request<T>(retryConfig);
          } catch (retryError: any) {
            // If still 403 after refresh, it's a permission issue
            if (retryError?.response?.status === 403) {
              console.warn('⚠️ Still 403 after refresh - insufficient permissions');
            }
            throw retryError;
          }
        }
        // If refresh failed, fall through to let error propagate
      }

      // ✅ Handle 401 Unauthorized - Token Refresh Flow
      // 1. Catch 401 error from backend
      // 2. Call refreshToken() → delegates to keycloak.updateToken()
      // 3. Keycloak uses refresh token to get new access token
      // 4. If successful: retry request with new token
      // 5. If refresh token expired: redirect to login
      if (error?.response?.status === 401 && !skipErrorHandler) {
        console.log('⚠️ 401 Unauthorized - attempting token refresh via Keycloak...');
        
        // Prevent infinite retry loop - check if this is already a retry
        const isRetry = (requestConfig as any)._isRetry;
        if (isRetry) {
          console.error('❌ Already retried after token refresh - refresh token likely expired');
          await this.logout();
          window.location.href = '/user/login';
          throw error;
        }
        
        // Handle concurrent refresh attempts - use shared promise
        let refreshed = false;
        if (this.isRefreshing && this.refreshPromise) {
          console.log('🔄 Token refresh already in progress, waiting...');
          refreshed = await this.refreshPromise;
        } else if (!this.isRefreshing) {
          this.isRefreshing = true;
          this.refreshPromise = this.refreshToken();
          try {
            refreshed = await this.refreshPromise;
          } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
          }
        }
        
        if (refreshed) {
          // Retry request with new token
          const newToken = this.getToken();
          if (newToken) {
            console.log('✅ Token refreshed successfully, retrying request...');
            const newHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
            // Mark this as a retry to prevent infinite loops
            const retryConfig = { 
              ...requestConfig, 
              headers: newHeaders
            };
            (retryConfig as any)._isRetry = true;
            return httpClient.request<T>(retryConfig);
          }
        } else {
          // Refresh failed - could be:
          // 1. Refresh token expired (normal expiry)
          // 2. Session terminated from Keycloak admin (admin termination)
          // 3. Network error (temporary)
          console.error('❌ Token refresh failed - session likely terminated or expired');
          
          // Check if this is a session termination (refresh token invalid)
          // by trying to get current token - if it's null, session is definitely terminated
          const currentToken = this.getToken();
          if (!currentToken) {
            console.warn('🚪 No token available - session terminated from Keycloak');
          }
          
          // Clear auth and redirect to login
          await this.logout();
          window.location.href = '/user/login';
        }
      }

      throw error;
    }
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    httpClient.clearCache();
  }

  /**
   * Clear cache for specific pattern
   */
  clearCachePattern(pattern: string): void {
    httpClient.clearCachePattern(pattern);
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; maxSize: number; hitRate: number } {
    return httpClient.getCacheStats();
  }
}

export const apiClient = new ApiClient();
export default apiClient;

